import { type FC, type PropsWithChildren, type ReactNode } from 'react'
import { type Booking, type Location, type SchedulingEventType } from '../../../models/Scheduling'
import { getBookingLocationLine } from '../bookingFormat'
import { BookingTimeBlock } from './BookingTimeBlock'
import { ContactChips } from './ContactChips'
import { EventTypeChip } from './EventTypeChip'
import { ProviderBadge } from './ProviderBadge'

export interface BookingRowProps {
  booking: Booking
  eventType?: SchedulingEventType
  location?: Location
  // Left-hand slot. Omit it for the default date/time block, pass a node to replace it (e.g. the
  // SENT n DAYS AGO block on Waiting rows), or pass null for rows with no left block.
  timeBlock?: ReactNode
  // Replaces the default booking summary (title + clients · event type · place). Tabs whose Figma
  // layout diverges from the default row (Suggested, Waiting) pass their own summary.
  summary?: ReactNode
}

// Shared row chassis for every Scheduling tab: optional time block on the left, booking summary
// in the middle, tab-specific actions on the right (passed as children).
export const BookingRow: FC<PropsWithChildren<BookingRowProps>> = ({ booking, eventType, location, timeBlock, summary, children }) => {
  const address = getBookingLocationLine(booking, location)
  return (
    <div className='d-flex align-items-center p-3 gap-3 border rounded zocks-border-color-gray-200 zocks-bg-color-white'>
      {timeBlock === undefined ? <BookingTimeBlock booking={booking} /> : timeBlock}
      {summary ?? (
        <div className='d-flex flex-column flex-grow-1 zocks-gap-0.25'>
          <div className='bold'>{booking.title ?? eventType?.name}</div>
          <div className='d-flex align-items-center flex-wrap zocks-gap-0.5'>
            <ContactChips clients={booking.clients} />
            <span className='zocks-text-gray-600'>·</span>
            <EventTypeChip eventType={eventType} />
            <span className='zocks-text-gray-600'>·</span>
            <ProviderBadge meetingMode={booking.meetingMode} meetingUrl={booking.meetingUrl} />
          </div>
          {booking.rescheduledBy || address ? (
            <div className='d-flex align-items-center flex-wrap zocks-gap-0.5 paragraph-small zocks-text-gray-500'>
              {booking.rescheduledBy ? <span>Rescheduled by {booking.rescheduledBy}</span> : null}
              {address ? (
                <span className='d-inline-flex align-items-center zocks-gap-0.25'>
                  <i className='bi bi-geo-alt' />
                  {address}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
      <div className='d-flex align-items-center gap-2'>{children}</div>
    </div>
  )
}
